import { Components, Theme } from '@mui/material';
import type { ReactNode } from 'react';
import { LelyBorderWidth } from '@lely/tokens/enums';

export const getFormComponents = (
  theme: Theme,
): Pick<
  Components,
  | 'MuiFormControlLabel'
  | 'MuiInputLabel'
  | 'MuiFilledInput'
  | 'MuiFormHelperText'
  | 'MuiInputAdornment'
  | 'MuiAutocomplete'
> => {
  const fieldHeight = theme.lelySpacing._8XL;
  const fieldPaddingY = theme.lelySpacing.SM;
  const fieldPaddingX = theme.lelySpacing.MD;
  const labelRowHeight = theme.lelyLineHeight.BASE;
  const borderWidthDefault = LelyBorderWidth.BASE;
  const borderWidthFocus = LelyBorderWidth.LG;

  return {
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          alignItems: 'center',
          gap: theme.lelySpacing.SM,
          marginLeft: 0,
          marginRight: 0,
          paddingTop: theme.lelySpacing.MD,
          paddingBottom: theme.lelySpacing.MD,

          '& .MuiFormControlLabel-label': {
            display: 'inline-flex',
            alignItems: 'center',
          },

          '&:has([data-dense="true"])': {
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
      },
    },
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          boxSizing: 'border-box',
          height: fieldHeight,
          gap: theme.lelySpacing.SM,

          ...theme.typography.xs,
          lineHeight: labelRowHeight,
          color: theme.palette.text.default,

          backgroundColor: theme.palette.background.light,
          border: `${borderWidthDefault} solid ${theme.palette.border.default}`,
          borderRadius: theme.shape.borderRadius,

          '&, &:hover, &.Mui-focused, &.Mui-disabled': {
            backgroundColor: theme.palette.background.light,
          },

          '&.MuiInputBase-adornedStart': {
            paddingLeft: 0,
          },

          '&.MuiInputBase-adornedEnd': {
            paddingRight: 0,
          },

          '&:hover:not(.Mui-disabled):not(.Mui-focused)': {
            borderWidth: borderWidthDefault,
            borderColor: theme.palette.border.primaryHover,
          },

          '&:active:not(.Mui-disabled):not(.Mui-focused)': {
            borderWidth: borderWidthDefault,
            borderColor: theme.palette.border.primaryActive,
          },

          '&.Mui-disabled': {
            opacity: theme.palette.action.disabledOpacity,
          },

          '&.Mui-error': {
            borderColor: theme.palette.border.error,
          },
        },
        input: ({
          ownerState,
        }: {
          ownerState: {
            hiddenLabel?: boolean;
            startAdornment?: ReactNode;
            endAdornment?: ReactNode;
          };
        }) => ({
          boxSizing: 'border-box',
          minHeight: labelRowHeight,
          lineHeight: labelRowHeight,
          color: theme.palette.text.default,
          paddingTop: ownerState.hiddenLabel
            ? fieldPaddingY
            : `calc(${fieldPaddingY} + ${labelRowHeight})`,
          paddingRight: ownerState.endAdornment ? 0 : fieldPaddingX,
          paddingBottom: fieldPaddingY,
          paddingLeft: ownerState.startAdornment ? 0 : fieldPaddingX,

          '&::placeholder': {
            color: theme.palette.text.subtle,
            opacity: 1,
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        filled: {
          ...theme.typography.xs,
          lineHeight: labelRowHeight,
          color: theme.palette.text.subtle,
          zIndex: 1,
          pointerEvents: 'none',
          // Resting: vertically centered in the 48px field
          transform: `translate(${fieldPaddingX}, calc((${fieldHeight} - ${labelRowHeight}) / 2)) scale(1)`,

          '&.MuiInputLabel-shrink': {
            ...theme.typography.xs,
            lineHeight: labelRowHeight,
            color: theme.palette.text.subtle,
            transform: `translate(${fieldPaddingX}, ${fieldPaddingY}) scale(1)`,
            pointerEvents: 'auto',
          },

          '&.Mui-focused, &.Mui-error': {
            color: theme.palette.text.subtle,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          ...theme.typography.xs,
          color: theme.palette.text.subtle,
          marginLeft: 0,
          marginRight: 0,
          marginTop: theme.lelySpacing._2XS,

          '&.Mui-error': {
            color: theme.palette.error.main,
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          ...theme.typography.xs,
          lineHeight: labelRowHeight,
          color: theme.palette.text.subtle,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        input: {
          all: 'unset',
          width: '100% !important',
          padding: `0px !important`,
        },

        inputRoot: {
          padding: theme.lelySpacing.LG,
          paddingInline: 0,
        },
      },
    },
  };
};
